import { z } from "zod";

/**
 * Request body for `POST /api/coach-manager/contracts/search`. Mirrors
 * `ContractSearchCriteria` (`src/lib/coach-manager/types.ts`) field-for-field
 * — `buildContractSearchEnvelope` fills the WSDL-required defaults for the
 * two ID fields and the two search operators, so every criteria field stays
 * optional here too.
 */
export const contractSearchRequestSchema = z.object({
  buInstanceId: z.string().min(1, "This field is required"),
  contractId: z.number().int().optional(),
  contractMovementId: z.number().int().optional(),
  pickup: z.string().optional(),
  destination: z.string().optional(),
  clientReference1: z.string().optional(),
  clientReference2: z.string().optional(),
  clientId: z.string().optional(),
  surname: z.string().optional(),
  initials: z.string().optional(),
  company: z.string().optional(),
  postCode: z.string().optional(),
  dateCreated: z.string().optional(),
  dateCreatedOperator: z.enum(["GreaterThan", "EqualTo", "LessThan"]).optional(),
  lastUpdated: z.string().optional(),
  lastUpdatedOperator: z.enum(["GreaterThan", "EqualTo", "LessThan"]).optional(),
});

export type ContractSearchRequest = z.infer<typeof contractSearchRequestSchema>;

// ── Full Contract write-shape ──
//
// Mirrors `Contract` (`src/lib/coach-manager/types.ts`) field-for-field,
// including its full nesting. Unlike `contractSearchRequestSchema` above
// (search criteria), this validates a Contract a user has EDITED in the
// Copy Operations staging editor and sent back to the server — a genuine
// external boundary (client → server), so it's parsed here rather than cast.
// Keep this in sync with `types.ts` if the wire shape ever changes.

const contractDriverSchema = z.object({
  ContractDriverID: z.number(),
  ContractVehicleID: z.number(),
  DriverType: z.string().optional(),
  DefaultDriverID: z.string().optional(),
});

const contractVehicleSchema = z.object({
  ContractVehicleID: z.number(),
  ContractMovementID: z.number(),
  Seats: z.number(),
  VehicleType: z.string().optional(),
  Revenue: z.number(),
  DefaultVehicleID: z.string().optional(),
  ContractDrivers: z.array(contractDriverSchema).optional(),
});

const contractPickupSchema = z.object({
  ContractPickupID: z.number(),
  ContractMovementID: z.number(),
  PickupPoint: z.string().optional(),
  ArrivalDayNo: z.number(),
  ArrivalTime: z.string().optional(),
  PickupDayNo: z.number(),
  PickupTime: z.string().optional(),
  Latitude: z.string().optional(),
  Longitude: z.string().optional(),
  Instructions: z.string().optional(),
});

const contractSetdownSchema = z.object({
  ContractSetdownID: z.number(),
  ContractMovementID: z.number(),
  SetdownPoint: z.string().optional(),
  ArrivalDayNo: z.number(),
  ArrivalTime: z.string().optional(),
  SetdownDayNo: z.number(),
  SetdownTime: z.string().optional(),
  Latitude: z.string().optional(),
  Longitude: z.string().optional(),
  Instructions: z.string().optional(),
});

const contractDateSchema = z.object({
  StartDate: z.string().optional(),
  ContractMovementID: z.number(),
});

const contractFacilitySchema = z.object({
  ContractMovementID: z.number(),
  Facility: z.string().optional(),
});

const contractIncludedItemSchema = z.object({
  ContractMovementID: z.number(),
  Item: z.string().optional(),
  Included: z.boolean(),
  Excluded: z.boolean(),
});

const contractDataSchema = z.object({
  ContractID: z.number(),
  ContractDataTitleSerialNo: z.number(),
  TextValue: z.string().optional(),
  NumberValue: z.number(),
  MoneyValue: z.number(),
  DateValue: z.string().optional(),
  YesNoValue: z.boolean(),
});

const contractMovementDataSchema = z.object({
  ContractMovementID: z.number(),
  ContractMovementDataTitleSerialNo: z.number(),
  TextValue: z.string().optional(),
  NumberValue: z.number(),
  MoneyValue: z.number(),
  DateValue: z.string().optional(),
  YesNoValue: z.boolean(),
});

const contractMovementSchema = z.object({
  ContractMovementID: z.number(),
  ContractID: z.number(),
  MovementDescription: z.string().optional(),
  StartBaseSerialNo: z.number(),
  StartTime: z.string().optional(),
  PositionDay: z.number(),
  PositionTime: z.string().optional(),
  PickupDay: z.number(),
  PickupTime: z.string().optional(),
  Pickup: z.string().optional(),
  PickupLatitude: z.string().optional(),
  PickupLongitude: z.string().optional(),
  PickupInstructions: z.string().optional(),
  SingleJourney: z.boolean(),
  VehicleToStay: z.boolean(),
  Destination: z.string().optional(),
  DestinationLatitude: z.string().optional(),
  DestinationLongitude: z.string().optional(),
  DestinationInstructions: z.string().optional(),
  ArrivalDay: z.number(),
  ArrivalTime: z.string().optional(),
  LeaveDay: z.number(),
  LeaveTime: z.string().optional(),
  BackDay: z.number(),
  BackTime: z.string().optional(),
  FinishDay: z.number(),
  FinishTime: z.string().optional(),
  FinishBaseSerialNo: z.number(),
  Passengers: z.number(),
  Distance: z.string().optional(),
  EmptyDistance: z.string().optional(),
  ClientReference1: z.string().optional(),
  ClientReference2: z.string().optional(),
  Route: z.string().optional(),
  FurtherRequirements: z.string().optional(),
  Notes: z.string().optional(),
  LastUpdated: z.string().optional(),
  Cancelled: z.boolean(),
  ContractDates: z.array(contractDateSchema).optional(),
  ContractVehicles: z.array(contractVehicleSchema).optional(),
  ContractPickups: z.array(contractPickupSchema).optional(),
  ContractSetdowns: z.array(contractSetdownSchema).optional(),
  ContractFacilities: z.array(contractFacilitySchema).optional(),
  ContractIncludedItems: z.array(contractIncludedItemSchema).optional(),
  ContractMovementData: z.array(contractMovementDataSchema).optional(),
});

export const contractSchema = z.object({
  ContractID: z.number(),
  ClientID: z.string().optional(),
  BookingType: z.string().optional(),
  CompanyID: z.string().optional(),
  BaseSerialNo: z.number(),
  OperatorID: z.string().optional(),
  DateCreated: z.string().optional(),
  Cancelled: z.boolean(),
  ContractMovements: z.array(contractMovementSchema).optional(),
  ContractData: z.array(contractDataSchema).optional(),
});
